import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  QrCode,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  Repeat2
} from 'lucide-react';
import { eventAPI } from '../../../services/api';
import api from '../../../services/api';
import { showToast } from '../../../utils/toasts';
import { format } from 'date-fns';
import { formatTime } from '../../../utils/timeFormat';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import LimitReachedModal from '../../../components/modals/LimitReachedModal';
import { useAuth } from '../../../context/AuthContext';
import { useResourceLimit } from '../../../hooks/useResourceLimit';
import PermissionGuard from '../../../components/guards/PermissionGuard';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery';
import { useBranch } from '../../../context/BranchContext';
import { usePageTour } from '../../../hooks/usePageTour';
import Loader from '../../../components/ui/Loader';

interface AllEventsProps {
  mode: 'services' | 'events';
}

const EVENT_TYPES_EXCLUDE_SERVICE = 'conference,seminar,workshop,social,outreach,meeting,other';

const AllEvents = ({ mode }: AllEventsProps) => {
  const navigate = useNavigate();
  usePageTour('events');
  const { user, fetchAndUpdateSubscription } = useAuth();
  const { selectedBranch } = useBranch();
  const plan = user?.merchant?.subscription?.plan;
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [usageData, setUsageData] = useState<any>(null);
  const eventLimit = useResourceLimit('events');

  const isServices = mode === 'services';
  const label = isServices ? 'Service' : 'Event';
  const labelPlural = isServices ? 'Services' : 'Events';
  const eventTypeParam = isServices ? 'service' : EVENT_TYPES_EXCLUDE_SERVICE;

  // Events fetcher for SWR
  const eventsFetcher = async (params: any) => {
    const response = await eventAPI.getEvents({
      ...params,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      eventType: eventTypeParam,
      search: searchQuery || undefined
    });

    return {
      items: response.data.data.events,
      pagination: {
        currentPage: response.data.data.pagination?.currentPage || 1,
        pages: response.data.data.pagination?.pages || 1,
        totalItems: response.data.data.pagination?.total || response.data.data.events.length,
      },
    };
  };

  const {
    data: events,
    loading,
    currentPage,
    totalPages,
    totalItems: totalEvents,
    setPage,
    refetch: refetchEvents
  } = usePaginatedQuery<any>(
    `${isServices ? 'services' : 'events'}-${selectedBranch?._id || 'all'}`,
    eventsFetcher,
    { limit: 10 }
  );

  useEffect(() => {
    fetchUsageData();
  }, [mode]);

  const fetchUsageData = async () => {
    try {
      const response = await api.get('/merchants/usage');
      if (response.data.success) {
        setUsageData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    }
  };

    const handleDelete = (event: any) => {
        setEventToDelete(event);
        setShowDeleteModal(true); 
    };

    const confirmDelete = async () => {
        setDeleting(true);
        await eventAPI.deleteEvent(eventToDelete._id);
        await fetchUsageData();
        showToast.success(`${label} deleted successfully`);
        setShowDeleteModal(false);
        setDeleting(false);
        setEventToDelete(null);
        refetchEvents();
    };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      published: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400',
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  const formatEventDate = (date: string) => {
    if (!date || isNaN(Date.parse(date))) return 'Invalid date';
    return format(new Date(date), 'MMM dd, yyyy');
  };



  const handleAddEventClick = () => {
    if (!eventLimit?.canCreate) {
      setShowLimitModal(true);
      return;
    }
    navigate(isServices ? '/services/new' : '/events/new');
  };

  return (
    <div className="p-2 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {labelPlural}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isServices
              ? 'Manage your regular church services and track attendance'
              : 'Manage your church events and track attendance'}
          </p>
        </div>
        <PermissionGuard permission="events.create">
        <div className="hidden md:flex flex-col items-end space-y-2">
          <button
            onClick={handleAddEventClick}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center shadow-md"
          >
            <Plus className="w-5 h-5 mr-2" />
            New {label}
          </button>
          {usageData?.events && (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {usageData.events.current} / {usageData.events.limit || '∞'} used
            </p>
          )}
        </div>
        </PermissionGuard>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${labelPlural.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">

        {/* Summary bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {loading
              ? 'Loading…'
              : `Showing (${totalEvents.toLocaleString()}) ${totalEvents === 1 ? label.toLowerCase() : labelPlural.toLowerCase()}`}
          </span>
          {!loading && totalPages > 1 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <Loader variant="skeleton-table" count={10} />
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No {labelPlural.toLowerCase()} found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first {label.toLowerCase()} to get started
            </p>
            <PermissionGuard permission="events.create">
            <button
              onClick={() => navigate(isServices ? '/services/new' : '/events/new')}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              New {label}
            </button>
            </PermissionGuard>
          </div>
        ) : (
          <>
            {/* Mobile Card List */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {events.map((event) => (
                <div
                  key={event._id}
                  onClick={() => navigate(`/${isServices ? 'services' : 'events'}/${event._id}`)}
                  className="p-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {event.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {event?.isRecurring ? formatEventDate(event.recurrence?.startDate) : formatEventDate(event.eventDate)}
                        {' · '}
                        {event?.isRecurring ? formatTime(event.recurrence?.baseTime || event.startTime) : formatTime(event.startTime)}
                      </p>
                      <span className={`inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium capitalize rounded-full ${getStatusBadge(event.status)}`}>
                        {event.status}
                      </span>
                    </div>
                    <div className="flex items-start gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <PermissionGuard permission="events.edit">
                        <button
                          onClick={() => navigate(`/${isServices ? 'services' : 'events'}/${event._id}/edit`)}
                          className="p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="events.delete">
                        <button
                          onClick={() => handleDelete(event)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                      {label}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                      Attendance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {events.map((event) => (
                    <tr
                      key={event._id}
                      onClick={() => navigate(`/${isServices ? 'services' : 'events'}/${event._id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      {/* Event */}
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center mr-3">
                            <Calendar className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {event.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Date & Time */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                            
                           { event?.isRecurring ? formatEventDate(event.recurrence?.startDate) : formatEventDate(event.eventDate)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                          <Clock className="w-3 h-3 mr-1" />
                          { event?.isRecurring ? formatTime(event.recurrence?.baseTime || event.startTime) : formatTime(event.startTime)}
                          {((event.isRecurring && event.recurrence?.baseEndTime) || event.endTime) && 
                            ` - ${formatTime(event.isRecurring ? event.recurrence?.baseEndTime : event.endTime)}`}
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-6 py-4">
                        <div className="flex items-start text-sm text-gray-900 dark:text-gray-100">
                          <MapPin className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5 text-gray-400" />
                          <span className="line-clamp-2">
                            {event.location?.venue || event.branch?.name || 'Not specified'}
                          </span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {event.isRecurring ? (
                            <>
                              <Repeat2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                                Recurring
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-500 dark:text-gray-400">One-time</span>
                          )}
                        </div>
                      </td>

                      {/* Attendance */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {event.stats.totalAttended} Attendees
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium capitalize rounded-full ${getStatusBadge(event.status)}`}>
                          {event.status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <PermissionGuard permission="events.edit">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/${isServices ? 'services' : 'events'}/${event._id}/edit`); }}
                            className="p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          </PermissionGuard>
                          <PermissionGuard permission="events.delete">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(event); }}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          </PermissionGuard>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col md:flex-row items-center gap-3 md:gap-0 md:justify-between">
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => currentPage > 1 && setPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => currentPage < totalPages && setPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

        <ConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={`Delete ${label}`}
            message={`Are you sure you want to delete "${eventToDelete?.title}"? This will permanently remove the ${label.toLowerCase()} and all associated data including attendance records, check-ins, registrations, and SMS logs. This action cannot be undone.`}
            type="danger"
            isLoading={deleting}
        /> 

        {/* Limit Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        resourceType="events"
        planName={plan}
        current={eventLimit?.current}
        limit={eventLimit?.limit || 0}
      />

      {/* Mobile: Floating New Event/Service Button */}
      <PermissionGuard permission="events.create">
        <button
          onClick={handleAddEventClick}
          className="md:hidden fixed right-4 z-30 w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-colors"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
          title={`New ${label}`}
        >
          <Plus className="w-6 h-6" />
        </button>
      </PermissionGuard>
    </div>
  );
};

export default AllEvents;